import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useFieldArray, useFormContext } from "react-hook-form";


import AuthButton from "components/base/AuthButton";
import BaseCreationStepContentByConfig from "components/base/BaseCreationStepContentByConfig";
import { SectionTopTitle } from "components/base/CommonComponent";
import BasePrompt from "components/base/prompt/BasePrompt";
import { SingleConditionRow } from "components/campaign/campaignCreation/AdditionalQuestionBar";
import TypeSelector from "components/campaignStreamline/TypeSelector";
import { EarningRuleRewardType, EarningRuleType } from "config/CustomEnums";
import { getEarningRuleCreationConfig } from "containers/engagement/campaignStreamline/CreateCampaignEarningPageConfig";


import EarningRuleGerneralPurchase from 'assets/images/earning/general_purchase.png';
import EarningRuleQRCode from 'assets/images/earning/qrcode_scanning.png';
import EarningRuleGPS from 'assets/images/earning/gps_check_in.png';
import EarningRuleReferral from 'assets/images/earning/referral.png';
import { tierTypeConfig } from "config/TierTypeConfig";
import ReactHookFormTextInput from "components/reactHookFormComponents/ReactHookFormTextInput";
import {  ReactHookFormErrorMessage } from "components/base/ErrorFieldMessage";

import "./StampCampaignTasks.scss"

export const earningRuleImageMap = {
  [EarningRuleType.generalPurchase]: EarningRuleGerneralPurchase,
  [EarningRuleType.qrCodeScanning]: EarningRuleQRCode,
  [EarningRuleType.gpsCheckIn]: EarningRuleGPS,
  [EarningRuleType.memberReferral]: EarningRuleReferral,
}

const getEarningRuleTypeSection = (fieldPrefix) => (
  {
    sectionTitle: 'RULE',
    sectionContent: [
      {
        key: 'earning_rule_type_key',
        complexComponent: ({ formContext: { watch, setValue } }) => (
          <TypeSelector
            typeConfig={[
              {
                id: EarningRuleType.generalPurchase,
                title: 'Purchase',
                image: earningRuleImageMap[EarningRuleType.generalPurchase],
                description: 'Customers can get reward after making purchase',
              },
              {
                id: EarningRuleType.qrCodeScanning,
                title: 'QR code scanning',
                image: earningRuleImageMap[EarningRuleType.qrCodeScanning],
                description: 'Customers can get reward after scanning a specific QR code',
              },
              {
                id: EarningRuleType.gpsCheckIn,
                title: 'GPS check-in',
                image: earningRuleImageMap[EarningRuleType.gpsCheckIn],
                description: 'Customers can get reward after visiting a specific place and check-in',
              },
              {
                id: EarningRuleType.memberReferral,
                title: 'Referral',
                image: earningRuleImageMap[EarningRuleType.memberReferral],
                description: 'Reward referrer (and new member) after the new member signs up successfully',
              },
            ].filter((item) => tierTypeConfig[item.id])}
            typeFieldName={`${fieldPrefix}type`}
            customItemClassName='earning-rule-type-selector'
          />
        ),
      },
    ],
  }
)

const getEarningRuleGeneralSection = (fieldPrefix) => (
  {
    sectionTitle: 'general',
    sectionContent: [
      {
        key: 'earning_rule_name_key',
        component: ReactHookFormTextInput,
        componentProps: {
          withTranslation: true,
          prefix: fieldPrefix,
          valueFieldName: `generalName`,
          errorId: `${fieldPrefix}generalName`,
          title: 'name',
        },
      },
    ],
  })

const getStampTaskCreationContentConfig = (fieldPrefix) => {
  const simpleEarningRuleCreationConfig = getEarningRuleCreationConfig(fieldPrefix);
  return {
    ...simpleEarningRuleCreationConfig,
    getDisplayComponentKeys: ({ formContext }) => {
      const { watch } = formContext;
      const earningRuleType = watch(`${fieldPrefix}type`)
      switch (earningRuleType) {
        case EarningRuleType.generalPurchase:
        case EarningRuleType.qrCodeScanning:
        case EarningRuleType.gpsCheckIn:
        case EarningRuleType.memberReferral:
          const {
            displayNormalSectionComponents,
            displayLanguageSectionComponents
          } = simpleEarningRuleCreationConfig.getDisplayComponentKeys({ formContext })
          return { displayNormalSectionComponents, displayLanguageSectionComponents }
        default:
          return { displayNormalSectionComponents: [], displayLanguageSectionComponents: [] }
      }
    },
    normalSections: [
      EarningRuleType.generalPurchase,
      EarningRuleType.qrCodeScanning,
      EarningRuleType.gpsCheckIn,
      EarningRuleType.memberReferral,
    ].reduce((res, type) => {
      res[type] = simpleEarningRuleCreationConfig?.normalSections?.[type]
      return res
    }, {}),

    languageSections: []
  }
}

const getStampTaskCreationBasicConfig = (fieldPrefix) => ({
  getDisplayComponentKeys: ({ formContext: { watch } }) => {
    const earningRuleType = watch(`${fieldPrefix}type`)

    return {
      displayNormalSectionComponents: ["earning_rule_type_key"],
      displayLanguageSectionComponents: earningRuleType ? ["earning_rule_name_key"] : []
    }
  },
  normalSections: [getEarningRuleTypeSection(fieldPrefix)],
  languageSections: [getEarningRuleGeneralSection(fieldPrefix)],
})

const StampTaskPrompt = ({ show, title = "Add Task", onCancel, onConfirm, fieldPrefix, disableConfirmBtn }) => {
  const [showCancelPrompt, setShowCancelPrompt] = useState(false);
  const [earningRuleBasicConfig, earningRuleCreationConfig] = useMemo(()=>[
    getStampTaskCreationBasicConfig(fieldPrefix),
    getStampTaskCreationContentConfig(fieldPrefix)
  ],[fieldPrefix])
  return (
    <>
      <BasePrompt
        show={show}
        disableEnforceFocus
        customClass="stamp-task-prompt"
        title={title}
        otherBody={() =>
          <>
            <BaseCreationStepContentByConfig
              configuration={earningRuleBasicConfig}
              hidePreview
            />
            <BaseCreationStepContentByConfig
              configuration={earningRuleCreationConfig}
              hidePreview
            />
          </>
        }
        disableClickBackground={true}
        closeAction={() => setShowCancelPrompt(true)}
        leftButton={{
          text: 'Cancel',
          action: () => setShowCancelPrompt(true),
        }}
        rightButton={{
          text: "Confirm",
          action: onConfirm,
          disabled: disableConfirmBtn
        }}
      />
      <BasePrompt
        disableClickBackground
        customClass={'export-report-modal'}
        show={showCancelPrompt}
        closeAction={() => setShowCancelPrompt(false)}
        title={"Confirm to cancel?"}
        leftButton={{
          text: 'No',
          action: () => {
            setShowCancelPrompt(false)
          },
        }}
        rightButton={{
          text: 'Yes',
          action: () => {
            onCancel()
            setShowCancelPrompt(false)
          },
        }}
      />
    </>
  )
}


const StampCampaignTasks = ({
  title = "Tasks",
  description = "Click “Add Task” to add a task. Drag the added tasks to adjust display order freely.",
  valueFieldName = "stampTasks",
  fieldPrefix = "",
}) => {
  const tmpNewTaskFieldName = `${fieldPrefix || ""}tmpNewTask`
  const tmpEditTaskFieldName = `${fieldPrefix || ""}tmpEditTask`
  const completeValueFieldName = `${fieldPrefix || ""}${valueFieldName}`;

  const currentEditIndex = useRef();

  const { watch, setValue, control, formState: { errors }, setError, clearErrors } = useFormContext();
  const { fields: stampTasks, append, remove, update } = useFieldArray({
    control,
    name: completeValueFieldName,
  });
  
  const newTaskData = watch(tmpNewTaskFieldName);
  const taskEditData = watch(tmpEditTaskFieldName);

  const addPromptConfirmBtnClickable = useRef();
  const setNewTaskData = (data) => {
    setValue(tmpNewTaskFieldName, data, { shouldDirty: true })
  }

  const [showAddTaskPrompt, setShowAddTaskPrompt] = useState(false);
  const [showEditPrompt, setShowEditPrompt] = useState(false);
  const [showDeleteTaskPrompt, setShowDeleteTaskPrompt] = useState(false);
  const clearNewTaskData = () => setNewTaskData({})

  const clearEditTaskData = () => setValue(tmpEditTaskFieldName, {}, { shouldDirty: true })
  const closeAddTask = () => {
    setShowAddTaskPrompt(false)
  }
  const onAddNewTask = (data) => {
    append(data || {})
    closeAddTask()
  }

  const closeEditTask = () => {
    setShowEditPrompt(false)
    clearErrors(tmpEditTaskFieldName)
  }
  useEffect(() => {
    if (showAddTaskPrompt) {
      setNewTaskData({ 
        linkedStampCampaign: true,
        rewardType: EarningRuleRewardType.stamps,
        executionPriority: 1
      })
    }
    if (!showAddTaskPrompt) {
      setTimeout(() => {
        clearNewTaskData()
      }, 500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddTaskPrompt])

  useEffect(() => {
    if (!showEditPrompt) {
      setTimeout(() => {
        clearEditTaskData()
      }, 500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditPrompt])
  
  return <div>
    {title && <SectionTopTitle title={title} />}
    {description && <div className="tips-message">{description}</div>}
    {stampTasks?.map((task, index) =>
      <>
        <SingleConditionRow
          isError={errors?.[`${completeValueFieldName}`]?.[index]}
          image={earningRuleImageMap?.[task?.type] || null}
          title={task?.translations?.en?.generalName || task?.name}
          onEdit={() => {
            setValue(tmpEditTaskFieldName, task, { shouldDirty: true })
            const tmpTaskErrors = errors?.[`${completeValueFieldName}`]?.[index] || {}
            Object.entries(tmpTaskErrors).map(([key, value]) => {
              setError(`${tmpEditTaskFieldName}.${key}`, { type: value?.type, message: value?.message})
            })
            currentEditIndex.current = index
            setShowEditPrompt(true)
          }}
          onRemove={() => {
            currentEditIndex.current = index
            setShowDeleteTaskPrompt(true)
          }}
        />
      </>
    )}
    <AuthButton
      customClass="btn-back-button-common content-add-button"
      title="+ Add Task"
      action={() => {
        addPromptConfirmBtnClickable.current = true;
        setShowAddTaskPrompt(true)
      }}
    />
    <ReactHookFormErrorMessage errors={errors} id={completeValueFieldName} />
    
    <StampTaskPrompt
      show={showAddTaskPrompt}
      title="Add Task"
      onCancel={closeAddTask}
      onConfirm={() => {
        if (!addPromptConfirmBtnClickable.current) {
          return
        }
        addPromptConfirmBtnClickable.current = false;
        onAddNewTask({ ...newTaskData })
      }}
      fieldPrefix={`${tmpNewTaskFieldName}.`}
      disableConfirmBtn={!watch(`${tmpNewTaskFieldName}.type`)}
    />
    <StampTaskPrompt
      show={showEditPrompt}
      title="Add Task"
      onCancel={closeEditTask}
      onConfirm={() => {
        update(currentEditIndex.current, taskEditData)
        setShowEditPrompt(false)
        clearErrors(tmpEditTaskFieldName)
      }}
      fieldPrefix={`${tmpEditTaskFieldName}.`}
      disableConfirmBtn={!watch(`${tmpEditTaskFieldName}.type`)}
    />
    <BasePrompt
      disableClickBackground
      customClass={'export-report-modal'}
      show={showDeleteTaskPrompt}
      closeAction={() => setShowDeleteTaskPrompt(false)}
      title={"Confirm to delete?"}
      leftButton={{
        text: 'Cancel',
        action: () => {
          setShowDeleteTaskPrompt(false)
        },
      }}
      rightButton={{
        text: 'Confirm',
        action: () => {
          if (currentEditIndex.current || currentEditIndex.current === 0) {
            remove(currentEditIndex.current)
          }
          setShowDeleteTaskPrompt(false)
        },
      }}
    />
  </div>
}

const mapPropsToState = (state) => ({
});

export default connect(mapPropsToState)(StampCampaignTasks);